import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

/**
 * Universal Import Data Modal
 * @param {string} importType - 'daily_inventory', 'repack_out', or 'shipments'
 */
function ImportDataModal({ open, onClose, onSuccess, user, importType = 'daily_inventory' }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  // ✅ NEW: Target storage location for admin imports
  const [targetStorage, setTargetStorage] = useState(''); // '', 'CS1', 'CS2'

  // Only admins can import
  if (user.role !== 'admin') {
    return null;
  }

  // Get API endpoint based on import type
  const getApiEndpoint = () => {
    switch (importType) {
      case 'repack_out':
        return 'https://ccc4cs.choicegroup.in/api/import_repack_out.php';
      case 'shipments':
        return 'https://ccc4cs.choicegroup.in/api/import_shipments.php';
      default:
        return 'https://ccc4cs.choicegroup.in/api/import_excel_IMPROVED.php';
    }
  };

  // Get template based on import type - UPDATED WITH PACK STYLE
  const getTemplateHeaders = () => {
    switch (importType) {
      case 'repack_out':
        return [
          'Production Date', 'Repack Out Date', 'Shift', 'Product ID', 'NAV ID', 
          'Pack Style', 'Product', 'Grade', 'Pack Size', 'Brand', 'Cartons', 
          'Loose Bags', 'Loose KGs', 'Trace ID', 'Day Code', 'Lot No', 'Pallet ID', 
          'CS1', 'CS2', 'Remarks', 'Total KGs', 'Total Pounds'
        ];
      case 'shipments':
        return [
          'FCL No', 'FCL Month', 'PO Number', 'Shipment Date', 'Production Date',
          'Shift', 'Product ID', 'NAV ID', 'Pack Style', 'Product', 'Grade', 
          'Pack Size', 'Brand', 'Cartons', 'Loose Bags', 'Loose KGs', 'Trace ID', 
          'Day Code', 'Lot No', 'Pallet ID', 'CS1', 'CS2', 'Remarks', 'Total KGs', 
          'Total Pounds'
        ];
      default: // daily_inventory - WITH PACK STYLE
        return [
          'Status', 'Production Date', 'Shift', 'Product ID', 'NAV ID', 'Pack Style', 
          'Product', 'Grade', 'Pack Size', 'Brand', 'Cartons', 'Loose Bags', 
          'Loose KGs', 'Trace ID', 'Day Code', 'Lot No', 'Pallet ID', 'CS1', 'CS2', 
          'Remarks', 'Total KGs', 'Total Pounds'
        ];
    }
  };

  const getTemplateSampleData = () => {
    switch (importType) {
      case 'repack_out':
        return [
          '2025-11-15', '2025-11-20', 'DAY', 'PROD001', 'NAV001', 'FINAL',
          'CPND', '16/20', '10/1 LB', 'SOUTHERN SEAS', '100', '5', '2.5', 
          'TR001', 'DC001', 'LOT123', 'PAL-001', 'CS-001', 'CS-002', 
          'Sample remark', '250.50', '552.25'
        ];
      case 'shipments':
        return [
          'FCL-001', 'NOV-2025', 'PO-12345', '2025-11-25', '2025-11-15', 
          'DAY', 'PROD001', 'NAV001', 'FINAL', 'CPND', '16/20', '10/1 LB', 
          'SOUTHERN SEAS', '100', '5', '2.5', 'TR001', 'DC001', 'LOT123', 
          'PAL-001', 'CS-001', 'CS-002', 'Sample remark', '250.50', '552.25'
        ];
      default: // daily_inventory - WITH PACK STYLE
        return [
          'PRODUCED', '2025-11-15', 'DAY', 'PROD001', 'NAV001', 'FINAL', 
          'CPND', '16/20', '10/1 LB', 'SOUTHERN SEAS', '100', '5', '2.5', 
          'TR001', 'DC001', 'LOT123', 'PAL-001', 'CS-001', 'CS-002', 
          'Sample remark', '250.50', '552.25'
        ];
    }
  };

  const getTitle = () => {
    switch (importType) {
      case 'repack_out':
        return 'Import Repack-Out Data';
      case 'shipments':
        return 'Import Shipments Data';
      default:
        return 'Import Daily Inventory Data';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['xlsx', 'xls', 'csv'];
      
      if (!validExtensions.includes(extension)) {
        setError('Invalid file type. Please upload Excel (.xlsx, .xls) or CSV (.csv) file');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    
    // ✅ NEW: Add target storage location if selected
    if (targetStorage) {
      formData.append('target_storage', targetStorage);
    }

    try {
      const response = await axios.post(
        getApiEndpoint(),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message,
          imported_count: response.data.imported_count
        });
        
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
          handleClose();
        }, 2000);
      } else {
        setResult({
          success: false,
          message: response.data.message,
          errors: response.data.errors || []
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = getTemplateHeaders();
    const sampleData = getTemplateSampleData();

    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType}_template_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setResult(null);
    setError('');
    setTargetStorage(''); // ✅ Reset target storage
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Import Instructions:</strong>
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Upload Excel (.xlsx, .xls) or CSV (.csv) file</li>
              <li>Date formats will be auto-detected</li>
              <li>Empty values are accepted</li>
              <li>Data will appear immediately after import</li>
            </ul>
          </Alert>

          {/* ✅ NEW: Target Storage Location Selection (for daily_inventory only) */}
          {importType === 'daily_inventory' && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>
                📦 Target Storage Location (Optional)
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 2, color: 'text.secondary' }}>
                Select a storage location to auto-fill CS1 or CS2 for all imported entries. Leave empty to use values from file.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={targetStorage === '' ? 'contained' : 'outlined'}
                  onClick={() => setTargetStorage('')}
                  size="small"
                  sx={{ minWidth: 140 }}
                >
                  📄 Use File Values
                </Button>
                <Button
                  variant={targetStorage === 'CS1' ? 'contained' : 'outlined'}
                  onClick={() => setTargetStorage('CS1')}
                  size="small"
                  color="success"
                  sx={{ minWidth: 100 }}
                >
                  🏭 CS1 Only
                </Button>
                <Button
                  variant={targetStorage === 'CS2' ? 'contained' : 'outlined'}
                  onClick={() => setTargetStorage('CS2')}
                  size="small"
                  color="warning"
                  sx={{ minWidth: 100 }}
                >
                  🏢 CS2 Only
                </Button>
              </Box>
              {targetStorage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  All imported entries will be assigned to <strong>{targetStorage}</strong> storage location.
                </Alert>
              )}
            </Box>
          )}

          {/* Download Template */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ textTransform: 'none' }}
            >
              Download Sample Template (Optional)
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Download a sample CSV template with correct column format
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* File Upload */}
          <Box sx={{ textAlign: 'center', py: 3, border: '2px dashed #ccc', borderRadius: 2, mb: 2 }}>
            <input
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
                size="large"
              >
                Browse File
              </Button>
            </label>
            
            {selectedFile && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="primary">
                  Selected: {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Size: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}
          </Box>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Result */}
          {result && result.success && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>{result.message}</strong>
              </Typography>
              <Typography variant="body2">
                Imported: {result.imported_count} records
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Refreshing data... Please wait.
              </Typography>
            </Alert>
          )}

          {/* Error Result */}
          {result && !result.success && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>{result.message}</strong>
              </Typography>
              {result.errors && result.errors.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Errors:
                  </Typography>
                  <List dense>
                    {result.errors.map((err, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {result && result.success ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || uploading}
          startIcon={<UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload & Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImportDataModal;